/**
 * WorkbenchPanel
 *
 * Right-side panel that wraps @ideoz/workbench components for the
 * conversation page. Receives streaming state from usePrototyperWorkbenchFacade
 * and renders the Workbench component with close/toggle controls.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  Workbench,
  workbenchStore,
  bootWebContainer,
  retryTemplateMount,
  getWebContainer,
  restartDevServerAfterSnapshot,
  retryPreviewRecovery,
  claimWorkbenchForThisTab,
  detectWorkbenchCapability,
  canOverrideWorkbenchTier,
  allowWorkbenchOnThisDevice,
  WORKBENCH_SAVE_CONFLICT_KEEP_MINE_EVENT,
  WORKBENCH_SAVE_CONFLICT_LOAD_LATEST_EVENT,
} from '@ideoz/workbench';
import { Button, Icon, cn } from '@ideoz/ui';
import { PrototyperLoadingAnimation, PrototyperLoadingIcon } from './PrototyperLoadingAnimation';
import { ChevronsLeft, ChevronsRight, Eye, Loader2 } from 'lucide-react';
import { useTrackEvent } from '@ideoz/core/analytics';
import { describeBuildActivity, type PrototyperBuildTimelineV2 } from '@ideoz/core';
import { notifyAppDataChanged, type WorkbenchLoadingPhase } from '@ideoz/workbench';
import { StartupDataView } from '../../startupData/StartupDataView';
import { useWorkbenchAppData } from '../../startupData/useWorkbenchAppData';
import { BuildProgressCard } from './BuildProgressCard';
import { WORKBENCH_BUILDER_SAVE_RETRY_EVENT } from '../utils/builderSave';

/** Preview waits that are a build running; the progress card replaces their generic line. */
const BUILD_PHASES = new Set<WorkbenchLoadingPhase>([
  'generating',
  'building',
  'applying-changes',
  'continuing-build',
]);

export interface WorkbenchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onToggleConversationPanel?: () => void;
  isConversationPanelCollapsed?: boolean;
  projectName?: string;
  /** Hide outer chrome when nested inside {@link PrototyperPanel} (publish uses legacy header). */
  embedded?: boolean;
  /** Forwarded to the embedded Workbench toolbar so the panel's header merges into it. */
  headerLeftSlot?: React.ReactNode;
  headerRightSlot?: React.ReactNode;
  headerRightLeadSlot?: React.ReactNode;
  /** The live build's timeline: the preview shows what the build is doing from it. */
  buildTimeline?: PrototyperBuildTimelineV2 | null;
  /** The startup (`projects.id`): its live data backs the preview and the Data view (US 596/598). */
  projectId?: string | null;
  /** The prototype open in the workbench (`chef_project_links.id`), for "This prototype". */
  prototypeId?: string | null;
}

export function WorkbenchPanel({
  isOpen,
  onClose,
  onToggleConversationPanel,
  isConversationPanelCollapsed,
  projectName,
  embedded = false,
  headerLeftSlot,
  headerRightSlot,
  headerRightLeadSlot,
  buildTimeline = null,
  projectId = null,
  prototypeId = null,
}: WorkbenchPanelProps) {
  useWorkbenchAppData(projectId);
  const dataView = useMemo(
    () =>
      projectId ? (
        <StartupDataView
          projectId={projectId}
          mode="prototype"
          prototypeId={prototypeId}
          onChanged={notifyAppDataChanged}
        />
      ) : undefined,
    [projectId, prototypeId]
  );
  const buildActivity = useMemo(() => describeBuildActivity(buildTimeline), [buildTimeline]);
  const buildStartedAt = useMemo(() => {
    let first: number | null = null;
    for (const seg of buildTimeline?.segments ?? []) {
      const t = seg.startedAt ? new Date(seg.startedAt).getTime() : NaN;
      if (!Number.isNaN(t) && (first === null || t < first)) first = t;
    }
    return first === null ? null : new Date(first).toISOString();
  }, [buildTimeline]);
  const renderPreviewLoading = ({
    message,
    variant,
    phase,
  }: {
    message: string;
    variant: 'generating' | 'plain';
    phase: WorkbenchLoadingPhase;
  }): React.ReactNode => {
    if (buildActivity && BUILD_PHASES.has(phase)) {
      return (
        <div className="flex flex-col items-center gap-2">
          <PrototyperLoadingAnimation />
          <BuildProgressCard activity={buildActivity} buildStartedAt={buildStartedAt} />
        </div>
      );
    }
    return variant === 'generating' ? (
      <PrototyperLoadingAnimation />
    ) : (
      <div className="flex flex-col items-center gap-3">
        <PrototyperLoadingAnimation />
        <p className="text-sm text-slate-600 max-w-sm">{message}</p>
      </div>
    );
  };
  const bootStatus = useStore(workbenchStore.bootStatus);
  const bootError = useStore(workbenchStore.bootError);
  const devServerStatus = useStore(workbenchStore.devServerStatus);
  const devServerDead = devServerStatus === 'crashed' || devServerStatus === 'error';
  const previewRecovery = useStore(workbenchStore.previewRecovery);
  const tabOwnership = useStore(workbenchStore.tabOwnership);
  /** Fixed for the page's life; "Try anyway" reloads to change it. */
  const capability = useMemo(() => detectWorkbenchCapability(), []);
  const { trackEvent } = useTrackEvent();
  const [restarting, setRestarting] = useState(false);
  const [claimingTab, setClaimingTab] = useState(false);

  /** On success this tab holds the lock; reload so the conversation boots and restores normally. */
  const claimWorkbenchHere = (): void => {
    if (claimingTab) return;
    setClaimingTab(true);
    void claimWorkbenchForThisTab()
      .then((result) => {
        if (result === 'owner') window.location.reload();
        else setClaimingTab(false);
      })
      .catch(() => setClaimingTab(false));
  };

  /**
   * `restartDevServerAfterSnapshot` resolves as soon as the new process spawns, well before
   * we know whether it actually came up — without waiting for a terminal `devServerStatus`
   * too, the button would flip back to idle mid-attempt with no way to tell the user whether
   * it worked. Guards against overlapping clicks queuing multiple restarts.
   */
  const restartPreview = (): void => {
    if (restarting) return;
    const wc = getWebContainer();
    if (!wc) return;
    setRestarting(true);
    void restartDevServerAfterSnapshot(wc)
      .catch(() => {
        /* devServerStatus already reflects the failure — nothing extra to do here */
      })
      .then(
        () =>
          new Promise<void>((resolve) => {
            const unsubscribe = workbenchStore.devServerStatus.subscribe((status) => {
              if (status === 'ready' || status === 'crashed' || status === 'error') {
                unsubscribe();
                resolve();
              }
            });
          })
      )
      .finally(() => setRestarting(false));
  };

  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ eventName?: string; properties?: Record<string, unknown> }>;
      const name = ce.detail?.eventName;
      if (!name) return;
      trackEvent(name, ce.detail.properties);
    };
    window.addEventListener('ideoz-hot-edit-analytics', handler);
    return () => window.removeEventListener('ideoz-hot-edit-analytics', handler);
  }, [trackEvent]);

  if (!isOpen) return null;

  const isCrossOriginIsolated = typeof window !== 'undefined' && window.crossOriginIsolated;

  /**
   * Boot/crash gates shared by the embedded and standalone layouts below — same conditions,
   * same markup either way. `Workbench` never mounts while one of these is showing, so there's
   * no risk of them competing with `Workbench`'s own loading-phase ladder for the same moment;
   * this is purely a dedup of what used to be two independently-maintained copies of this JSX.
   * Returns `null` once none of the gates apply, meaning it's time to render `<Workbench/>`.
   */
  const renderBootGate = (): React.ReactNode | null => {
    if (!isCrossOriginIsolated) {
      return (
        <div className="flex items-center justify-center h-full p-8 text-center">
          <div>
            <p className="text-lg font-semibold text-red-600 mb-2">WebContainer unavailable</p>
            <p className="text-sm text-slate-600">
              Cross-origin isolation is required. Ensure COOP/COEP headers are set on this origin.
            </p>
          </div>
        </div>
      );
    }
    if (capability.tier === 'view-only') {
      const lowMemory = canOverrideWorkbenchTier(capability);
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-slate-800">
            {lowMemory
              ? 'This device may not have enough memory for the prototype workspace'
              : 'Editing prototypes needs a desktop browser'}
          </p>
          <p className="text-sm text-slate-600 max-w-md">
            {lowMemory
              ? 'The workspace runs a full dev server inside your browser. Close other tabs and apps, then try anyway.'
              : 'Open this conversation in Chrome, Edge, Firefox or Safari on a computer to view and edit the prototype.'}
          </p>
          {lowMemory && (
            <Button
              type="button"
              variant="secondary-gray"
              size="sm"
              onClick={() => {
                allowWorkbenchOnThisDevice();
                window.location.reload();
              }}
            >
              Try anyway
            </Button>
          )}
        </div>
      );
    }
    if (
      tabOwnership === 'other-tab' ||
      tabOwnership === 'other-tab-busy' ||
      tabOwnership === 'released'
    ) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-slate-800">
            {tabOwnership === 'released'
              ? 'The workbench moved to another tab'
              : 'The workbench is open in other tabs'}
          </p>
          <p className="text-sm text-slate-600 max-w-md">
            {tabOwnership === 'other-tab-busy'
              ? 'That tab is still building a prototype. Use it here once the build finishes.'
              : 'This device already runs the prototype workspace in as many tabs as it can. Your prototype is saved.'}
          </p>
          <Button type="button" size="sm" onClick={claimWorkbenchHere} disabled={claimingTab}>
            {claimingTab ? 'Switching…' : 'Use it here'}
          </Button>
        </div>
      );
    }
    if (bootStatus === 'booting' || bootStatus === 'idle') {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          <p className="text-sm text-slate-600 text-center max-w-sm">
            Starting in-browser Node.js (WebContainer)…
          </p>
        </div>
      );
    }
    if (bootStatus === 'error') {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-red-600">Could not start WebContainer</p>
          <p className="text-sm text-slate-600 max-w-md">{bootError || 'Unknown error'}</p>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => {
              void retryTemplateMount().catch(() => {
                void bootWebContainer();
              });
            }}
          >
            Retry
          </Button>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </Button>
          <p className="text-xs text-slate-500 max-w-sm">
            A full reload clears duplicate boots after dev hot reload or multiple workbench starts.
          </p>
        </div>
      );
    }
    // Recovery restarts or reboots the environment and flips `devServerStatus` through
    // crashed/starting on the way, so it takes precedence over the "stopped" card below.
    if (previewRecovery.status === 'recovering') {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          <p className="text-sm font-medium text-slate-800">Reconnecting the preview…</p>
          <p className="text-sm text-slate-600 max-w-sm">
            {previewRecovery.step === 'reboot-webcontainer'
              ? 'Restarting the in-browser workspace. Your files are safe.'
              : 'Your files are safe — this can take a moment.'}
          </p>
        </div>
      );
    }
    if (previewRecovery.status === 'failed') {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-red-600">The preview couldn't restart</p>
          <p className="text-sm text-slate-600 max-w-md">
            Your prototype is saved. Reloading the page starts a fresh workspace; closing other tabs
            or apps frees memory on low-memory devices.
          </p>
          <Button type="button" size="sm" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => {
              void retryPreviewRecovery();
            }}
          >
            Try again
          </Button>
        </div>
      );
    }
    // `devServerStatus` flips to 'starting' the instant a restart begins, which would make
    // `devServerDead` false and drop this whole card (button included) before the user ever
    // sees the outcome — stay mounted through an active restart attempt too, not just while
    // genuinely dead.
    if (devServerDead || restarting) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-3">
          <p className="text-lg font-semibold text-red-600">
            {devServerDead ? 'Preview server stopped' : 'Restarting preview server…'}
          </p>
          <p className="text-sm text-slate-600 max-w-md">
            {devServerDead
              ? `The in-browser dev server ${devServerStatus === 'crashed' ? 'crashed' : 'did not respond in time'}. Your files are safe — restart the preview to continue.`
              : 'Your files are safe — this can take a moment.'}
          </p>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={restartPreview}
            disabled={restarting}
          >
            {restarting ? 'Restarting…' : 'Restart preview'}
          </Button>
          <Button
            type="button"
            variant="secondary-gray"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </Button>
        </div>
      );
    }
    return null;
  };

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0 h-full bg-slate-50 overflow-hidden">
        <SaveConflictBanner />
        <BuilderSaveStatus />
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderBootGate() ?? (
            <Workbench
              projectName={projectName}
              headerLeftSlot={headerLeftSlot}
              headerRightSlot={headerRightSlot}
              headerRightLeadSlot={headerRightLeadSlot}
              renderPreviewLoading={renderPreviewLoading}
              dataView={dataView}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-50">
      {/* Workbench toolbar — match PrototyperPanel toggle styling */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-100 border-b border-slate-200 gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {onToggleConversationPanel && (
            <Button
              type="button"
              onClick={onToggleConversationPanel}
              variant="ghost"
              size="sm"
              className="!p-1 h-auto border border-slate-300 gap-0.5 shrink-0"
              aria-label={
                isConversationPanelCollapsed ? 'Show conversation panel' : 'Hide conversation panel'
              }
            >
              <Icon
                icon={isConversationPanelCollapsed ? ChevronsRight : ChevronsLeft}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-primary-900 stroke-white'
                    : 'bg-white stroke-primary-900'
                )}
              />
              <Icon
                icon={Eye}
                size={24}
                className={cn(
                  'rounded-full p-1',
                  isConversationPanelCollapsed
                    ? 'bg-white stroke-primary-900'
                    : 'bg-primary-900 stroke-white'
                )}
              />
            </Button>
          )}
          <span className="text-sm font-medium text-slate-800 truncate">
            WebContainer workbench
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 rounded-md shrink-0"
          title="Close workbench"
        >
          Close
        </button>
      </div>

      <SaveConflictBanner />

      <BuilderSaveStatus />
      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {renderBootGate() ?? (
          <Workbench
            projectName={projectName}
            renderPreviewLoading={renderPreviewLoading}
            dataView={dataView}
          />
        )}
      </div>
    </div>
  );
}

/** How long "Edits saved" stays on screen. */
const BUILDER_SAVED_VISIBLE_MS = 3_000;

/**
 * Whether the founder's saved edits reached the builder's copy, which the next change starts
 * from (`utils/builderSave.ts`). Not saved means the next change may undo the edit, so it stays
 * on screen with a Retry.
 */
function BuilderSaveStatus(): React.ReactElement | null {
  const save = useStore(workbenchStore.builderSave);
  useEffect(() => {
    if (save?.status !== 'saved') return undefined;
    const timer = setTimeout(() => {
      if (workbenchStore.builderSave.get() === save) workbenchStore.builderSave.set(null);
    }, BUILDER_SAVED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [save]);
  if (!save) return null;
  if (save.status === 'failed') {
    return (
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-2 bg-red-50 border-b border-red-200 text-sm text-slate-800 shrink-0"
        role="alert"
        data-testid="workbench-builder-save-failed"
      >
        <span className="flex-1 min-w-0">
          Your edits were not saved for the next change. Retry, or the next change may undo them.
        </span>
        <Button
          type="button"
          size="sm"
          onClick={() => window.dispatchEvent(new CustomEvent(WORKBENCH_BUILDER_SAVE_RETRY_EVENT))}
        >
          Retry
        </Button>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-2 px-4 py-1.5 border-b border-slate-200 text-xs text-slate-600 shrink-0"
      role="status"
      data-testid="workbench-builder-save-status"
    >
      {save.status === 'saving' ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Saving your edits...
        </>
      ) : (
        'Edits saved'
      )}
    </div>
  );
}

/**
 * A manual-edit save was refused because another tab saved this turn after this tab loaded it
 * (P6.4e). The facade handles both choices (`WORKBENCH_SAVE_CONFLICT_*` events).
 */
function SaveConflictBanner(): React.ReactElement | null {
  const conflict = useStore(workbenchStore.saveConflict);
  if (!conflict) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-slate-800 shrink-0"
      data-testid="workbench-save-conflict"
    >
      <span className="flex-1 min-w-0">
        This prototype was saved in another tab after you opened it, so your latest edits were not
        saved.
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary-gray"
        onClick={() => window.dispatchEvent(new CustomEvent(WORKBENCH_SAVE_CONFLICT_LOAD_LATEST_EVENT))}
      >
        Load the latest
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={() => window.dispatchEvent(new CustomEvent(WORKBENCH_SAVE_CONFLICT_KEEP_MINE_EVENT))}
      >
        Keep my copy
      </Button>
    </div>
  );
}
